/**
 * LO STATO DI UNA PARTITA QGAMMON — `waiting` (appuntamento) o `active`.
 *
 * ═══ PERCHE' ESISTE QUESTO FILE ═══
 * ADR-0114: accettare un invito crea un APPUNTAMENTO, non una partita. Il
 * server lo dice su **quattro** canali distinti:
 *   1. `POST /invites/:code/accept` → `status: "waiting"` nella risposta;
 *   2. `invite:accepted` sul socket del CREATORE → stesso campo;
 *   3. `GET /matches?include=waiting` → `status` su ogni riga;
 *   4. **`match:state` sul socket della partita** → `status`, a ogni snapshot.
 * Il client non ne ascoltava nessuno, e per questo scriveva «In gioco» su una
 * partita che non era iniziata.
 *
 * ═══ 🔴 IL CAMPO ARRIVA, IL TIPO NON LO DICE ═══
 * [M or56, 2026-09-07] `@qlashgeminiz/qgammon-types@0.2.0` — la versione
 * installata — **non dichiara `status`** in `MatchStatePayload`: zero occorrenze
 * della parola in `dist/events.d.ts`. Il campo pero' **arriva a runtime**, e la
 * ragione e' verificabile in due punti:
 *   · il server lo mette in ogni snapshot
 *     (`qgammon-gameserver/src/game/payloads.ts:93` @ origin/main), col commento
 *     «A client MUST branch on it before it renders a sentence»;
 *   · il consumatore non lo toglie: `qgammon-board-ui@0.3.1`,
 *     `dist/index.js:1074` fa `socket.on(ServerEvents.State, (p) => setState(p))`
 *     — **nessun parse zod, nessuno strip di chiavi ignote**.
 * ⇒ Si legge con un allargamento di tipo LOCALE, in un punto solo, e questo file
 * e' quel punto. ⛔ Non spargere il cast: il giorno che i tipi dichiarano
 * `status`, qui si cancella una riga; sparso, si va a cercarlo.
 *
 * ⚠️ DEBITO APERTO, dichiarato: far dichiarare `status` a `qgammon-types` (repo
 * QGammon, non questo). Finche' non succede, `statusOfMatchState` e' l'unico
 * posto in cui questa app crede a un campo che i suoi tipi non conoscono.
 *
 * ⚠️ E il caso «il campo non c'e'» NON e' un errore: e' cio' che risponde un
 * gameserver piu' vecchio dell'ADR. Allora si torna al comportamento di prima —
 * *una partita* — perche' e' cio' che quel server sa produrre. `null` significa
 * «non me l'ha detto», e chi chiama decide cosa farne.
 */

/** Cio' che il server dice di una partita, quando lo dice. */
export type MatchStatus = 'waiting' | 'active';

/**
 * Legge `status` da uno snapshot `match:state`, o `null` se non c'e'.
 *
 * Accetta `unknown` di proposito: il tipo del pacchetto non ha il campo, quindi
 * un parametro tipato costringerebbe il chiamante al cast — cioe' sposterebbe
 * fuori esattamente la cosa che questo file esiste per tenere dentro.
 */
export function statusOfMatchState(state: unknown): MatchStatus | null {
  if (!state || typeof state !== 'object') return null;
  const raw = (state as { status?: unknown }).status;
  return raw === 'waiting' || raw === 'active' ? raw : null;
}

/**
 * L'appuntamento e' ancora in attesa?
 *
 * `snapshot` e' cio' che dice il socket (autorevole, ma arriva dopo il primo
 * disegno); `descriptor` e' cio' che sapeva chi ha aperto la schermata.
 *
 * 🔴 L'ORDINE NON E' ARBITRARIO, ed e' la meta' che conta: **il socket vince
 * appena parla**. E' cosi' che la schermata smette di dire «in attesa» nello
 * stesso istante in cui l'avversario arriva — il server manda un `match:state`
 * con `status: "active"` a tutta la stanza proprio in quel momento
 * (`game/handlers.ts:474-479`). Preferire il descrittore terrebbe la frase
 * «in attesa» su una partita gia' iniziata: la stessa bugia di prima, girata.
 */
export function isWaitingMatch(snapshot: unknown, descriptor?: MatchStatus): boolean {
  return (statusOfMatchState(snapshot) ?? descriptor ?? 'active') === 'waiting';
}

/**
 * 🔴 PERCHE' UNA PARTITA E' FINITA, QUANDO NON L'HA VINTA NESSUNO — letto dal
 * payload `match:ended` del socket, che questo campo lo porta e i tipi
 * installati non lo dichiarano (stessa faglia di `status`, terza occorrenza).
 *
 * ⚠️ IL FATTO CHE RENDE QUESTA LETTURA NECESSARIA, e non una raffinatezza:
 * `MatchEndedPayload` dichiara `winner: "W" | "B"` — **obbligatorio** — e il
 * server ce lo mette anche quando un vincitore NON C'E'. E' un **segnaposto
 * strutturale**, scritto cosi' apposta perche' i client gia' rilasciati lo
 * pretendono:
 *   `winner: "W" as Player, // Structural placeholder — the schema has always
 *    required a winner and shipped clients parse it. `abandonedReason` being
 *    present is what says there is none.`
 *   [`qgammon-gameserver/src/game/reaper.ts:118-131` @ origin/main]
 * ⇒ **un client che legge `winner` senza guardare qui ANNUNCIA UN VINCITORE**
 * su una partita mai giocata, e non c'e' nessun errore da nessuna parte.
 * [M or49 07/09: succede su TRE ragioni — `never_played`, `expired`, e ora
 * `cancelled`, la disdetta.]
 *
 * ⛔ Qualunque valore presente e' abbastanza per NON pronunciare un vincitore,
 * anche uno che non sappiamo tradurre: il campo esiste **solo** sulle righe
 * senza vincitore. Sapere COME dirlo e' un problema di copy; sapere di **non**
 * dover dire «hai vinto» no. ⇒ questa funzione torna la stringa grezza e
 * `matchOutcome` decide se sa etichettarla.
 */
export function abandonedReasonOf(ended: unknown): string | null {
  if (!ended || typeof ended !== 'object') return null;
  const raw = (ended as { abandonedReason?: unknown }).abandonedReason;
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

/**
 * IL CODICE DI RIFIUTO DELLE AZIONI SU UN APPUNTAMENTO (ADR-0114).
 *
 * Il gameserver rifiuta con `MATCH_NOT_STARTED` le azioni di gioco su un
 * appuntamento — cinque: tirare, muovere, raddoppiare, abbandonare il match,
 * abbandonare il game [or49, 07/09]. **Nessun client lo conosce**: oggi
 * mostrerebbero il messaggio grezzo del server, e `ErrorPayload.code` nei tipi
 * installati (0.2.0) e' un enum di otto valori che non lo contiene.
 * ⇒ si riconosce per stringa, in questo unico punto, come tutto il resto.
 */
export const MATCH_NOT_STARTED = 'MATCH_NOT_STARTED';

export function isMatchNotStarted(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return (error as { code?: unknown }).code === MATCH_NOT_STARTED;
}

/**
 * 🔴 UN `NOT_FOUND` SULL'INGRESSO NON E' UN ERRORE TRANSITORIO: E' UN VERDETTO.
 *
 * Misurato l'11/09 su `stg-tma` (`or63`): la lobby offriva «Riprendi» per una
 * partita che il server non aveva piu' (`GET /matches?include=waiting` →
 * `{"matches":[]}`), e la schermata restava su «Connessione alla partita…»
 * **per sempre**, con console senza errori.
 *
 * ⭐ Il segnale c'era e veniva BUTTATO VIA. `match:join` su un id che il registry
 * non ha risponde `GameError NOT_FOUND` su `ServerEvents.Error` [or49, sorgente
 * `game/handlers.ts:427-434`] — ma `useBackgammonSocket` di allora **azzerava
 * ogni errore dopo 4 secondi** (`board-ui@0.3.2`, `dist/index.js:1102-1107`:
 * `setTimeout(() => setError(cur => cur === p ? null : cur), 4e3)`).
 * ⇒ *l'avviso compariva per quattro secondi e poi non era mai esistito.*
 *
 * 🔑 ***Quell'azzeramento e' GIUSTO per la maggior parte dei codici*** —
 * `ILLEGAL_MOVE`, `NOT_YOUR_TURN`, `THROTTLED`, `WRONG_PHASE` descrivono un
 * tentativo, non la partita, e lasciarli a schermo sarebbe peggio — ***ed e'
 * sbagliato per `NOT_FOUND`, che descrive il SOGGETTO della schermata e non
 * cambiera' aspettando.*** Con `0.3.2` arrivavano dallo stesso evento e lo
 * stesso meccanismo li trattava uguali.
 * ⚠️ Con `board-ui@0.18.0` (il lock del 02/11) non e' piu' cosi':
 * `isTerminalErrorCode('NOT_FOUND', hasEnteredMatch)` e' terminale finche' non si
 * e' entrati (`dist/index.js:3172-3176`, `3418-3426`), e un errore terminale NON
 * si azzera: la riga resta a schermo per sempre (misurato 02/11, cantiere 53, a 6,5 s).
 * ⇒ Chi chiama lo **trattiene** lo stesso (v. l'uso in `BackgammonMatch`): il
 * fermo non dipende dalla versione dell'hook, e con `0.3.2` senza memoria al
 * quinto secondo la schermata tornava a «sto connettendo».
 *
 * ⛔ E NON si usa `GET /matches/:id` per rispondere alla stessa domanda: quella
 * rotta serve **solo i match FINITI** (`qgammon-api matches/routes.ts:89` →
 * `readFinishedMatch`, «only serve FINISHED matches — a live match's
 * rolls/moves/seeds must not leak») ⇒ **una partita VIVA risponde 404 esattamente
 * come una che non esiste.** Una guardia costruita su quel 404 direbbe «non c'e'
 * piu'» a chi sta giocando. *Prescritta da or49 l'11/09 e ritirata da lui stesso
 * dopo aver letto il proprio sorgente, prima che venisse scritta.*
 */
export const MATCH_GONE = 'NOT_FOUND';

export function isMatchGone(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  return (error as { code?: unknown }).code === MATCH_GONE;
}

/**
 * IL FERMO, come riduttore PURO — perche' il gate lo possa eseguire.
 *
 * `prev` e' lo stato del fermo, `error` l'errore corrente del socket, `hasGame`
 * se un tabellone e' mai arrivato. ⭐ **La riga che conta e' la terza**: con
 * `prev === true` la risposta e' `true` *qualunque cosa sia `error`* — ed era
 * esattamente il caso che l'hook di `board-ui@0.3.2` produceva quattro secondi
 * dopo, azzerandolo (con `0.18.0` il `NOT_FOUND` d'ingresso resta: v. sopra).
 *
 * ⛔ Non si arma a partita in corso: un `NOT_FOUND` con un tabellone a schermo
 * riguarda un'AZIONE, e coprirlo con un cartello «non e' disponibile» sarebbe
 * peggio dell'attesa che questo fermo esiste per chiudere.
 */
export function nextGoneLatch(prev: boolean, error: unknown, hasGame: boolean): boolean {
  if (prev) return true;
  return isMatchGone(error) && !hasGame;
}
